import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses, formatAmount } from "../theme";
import {
  apiErrorMessage,
  createPayment,
  deletePayment,
  fetchPaymentProofUrl,
  fetchPaymentReceipt,
  fetchPayments,
  runPayments,
  updatePayment,
  uploadPaymentProof,
} from "../services/payrollService";
import { fetchTeamMembers } from "../../team/services/teamService";
import type { PaymentKind, PaymentMethod, PayrollPayment } from "../types";

// ---------------------------------------------------------------------------
// Mis pagos (#1025): el historial de nóminas PAGADAS y el comprobante de cada
// pago. Cada colaborador ve solo lo suyo (lo acota el servidor, #1007); quien
// administra ve a todo el equipo, registra los pagos —uno por uno o la
// quincena completa desde el padrón— y sube el comprobante bancario.
//
// Hay dos documentos por pago:
//   • el COMPROBANTE: el archivo del banco / NVIO que subió administración;
//   • el RECIBO: un PDF que arma el servidor con los datos del pago (no es CFDI).
// ---------------------------------------------------------------------------

const KINDS: PaymentKind[] = ["salary", "bonus", "other"];
const METHODS: PaymentMethod[] = ["nvio", "spei", "transfer", "cash", "other"];
type Half = "Q1" | "Q2" | "M";

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => today().slice(0, 7);

/** Quincena en curso: la 1.ª hasta el día 15. */
const currentHalf = (): Half => (Number(today().slice(8, 10)) <= 15 ? "Q1" : "Q2");

interface MemberOption {
  id: string;
  name: string;
  currency: string;
  fixedSalary: number | null;
}

interface FormState {
  id?: string;
  teamMemberId: string;
  kind: PaymentKind;
  month: string;
  half: Half;
  paidAt: string;
  amount: string;
  currency: string;
  method: PaymentMethod;
  reference: string;
  notes: string;
  exchangeRate: string;
}

const emptyForm = (): FormState => ({
  teamMemberId: "",
  kind: "salary",
  month: thisMonth(),
  half: currentHalf(),
  paidAt: today(),
  amount: "",
  currency: "USD",
  method: "nvio",
  reference: "",
  notes: "",
  exchangeRate: "",
});

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function openUrl(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function PaymentsSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rows, setRows] = useState<PayrollPayment[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [year, setYear] = useState<string>(today().slice(0, 4));
  const [person, setPerson] = useState<string>("");
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [run, setRun] = useState<{ month: string; half: Half; paidAt: string; method: PaymentMethod; reference: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPayments();
      setRows(res.items);
      setIsAdmin(res.isStaffAdmin);
    } catch (err) {
      setError(apiErrorMessage(err) || t("payroll.payments.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // El padrón solo hace falta para registrar pagos.
  useEffect(() => {
    if (!isAdmin) return;
    fetchTeamMembers({ limit: 200 })
      .then(({ items }) =>
        setMembers(
          items
            .map((m: any) => ({
              id: m.id,
              name: [m.name, m.lastName].filter(Boolean).join(" ") || m.id,
              currency: String(m.currency || "MXN").toUpperCase(),
              fixedSalary: typeof m.fixedSalary === "number" ? m.fixedSalary : null,
              active: m.active !== false && (!m.status || m.status === "active"),
            }))
            .filter((m) => m.active)
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => setMembers([]));
  }, [isAdmin]);

  const monthName = useCallback(
    (ym: string) => {
      const [y, m] = ym.split("-").map(Number);
      if (!y || !m) return ym;
      return new Intl.DateTimeFormat(i18n?.language || "es", { month: "long", year: "numeric", timeZone: "UTC" }).format(
        new Date(Date.UTC(y, m - 1, 1)),
      );
    },
    [i18n?.language],
  );

  const periodLabel = useCallback(
    (period: string) => {
      const m = /^(\d{4}-\d{2})-(Q1|Q2|M)$/.exec(period || "");
      if (!m) return period;
      if (m[2] === "M") return monthName(m[1]);
      return `${t(m[2] === "Q1" ? "payroll.payments.half1" : "payroll.payments.half2")} · ${monthName(m[1])}`;
    },
    [monthName, t],
  );

  const years = useMemo(() => {
    const set = new Set(rows.map((r) => String(r.paidAt || "").slice(0, 4)).filter(Boolean));
    set.add(today().slice(0, 4));
    return [...set].sort().reverse();
  }, [rows]);

  const people = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) map.set(r.teamMemberId, r.teamMemberName || r.teamMemberId);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const visible = useMemo(
    () =>
      rows.filter(
        (r) => (!year || String(r.paidAt).startsWith(year)) && (!person || r.teamMemberId === person),
      ),
    [rows, year, person],
  );

  /** Total por moneda: no se suman dólares con pesos. */
  const totals = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const r of visible) acc[r.currency] = Math.round(((acc[r.currency] || 0) + Number(r.amount || 0)) * 100) / 100;
    return Object.entries(acc);
  }, [visible]);

  const fail = (err: unknown, key: string) => setNotice(apiErrorMessage(err) || t(key));

  const downloadReceipt = async (p: PayrollPayment) => {
    try {
      saveBlob(await fetchPaymentReceipt(p.id), `recibo-${p.period}.pdf`);
    } catch (err) {
      fail(err, "payroll.payments.downloadError");
    }
  };

  const downloadProof = async (p: PayrollPayment) => {
    try {
      openUrl((await fetchPaymentProofUrl(p.id)).url);
    } catch (err) {
      fail(err, "payroll.payments.downloadError");
    }
  };

  const pickProof = (id: string) => {
    uploadTarget.current = id;
    fileInput.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const id = uploadTarget.current;
    e.target.value = "";
    if (!file || !id) return;
    setBusy(true);
    try {
      await uploadPaymentProof(id, file);
      await load();
    } catch (err) {
      fail(err, "payroll.payments.saveError");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: PayrollPayment) => {
    const ok = await Swal.fire({
      title: t("payroll.payments.confirmDelete"),
      text: `${p.teamMemberName || ""} · ${periodLabel(p.period)}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("payroll.payments.delete"),
      cancelButtonText: t("payroll.payments.cancel"),
      confirmButtonColor: "#dc2626",
    });
    if (!ok.isConfirmed) return;
    try {
      await deletePayment(p.id);
      await load();
    } catch (err) {
      fail(err, "payroll.payments.saveError");
    }
  };

  const openEdit = (p: PayrollPayment) => {
    const m = /^(\d{4}-\d{2})-(Q1|Q2|M)$/.exec(p.period) || [];
    setForm({
      id: p.id,
      teamMemberId: p.teamMemberId,
      kind: p.kind,
      month: m[1] || thisMonth(),
      half: (m[2] as Half) || "Q1",
      paidAt: p.paidAt,
      amount: String(p.amount ?? ""),
      currency: p.currency,
      method: p.method,
      reference: p.reference || "",
      notes: p.notes || "",
      exchangeRate: p.exchangeRate ? String(p.exchangeRate) : "",
    });
  };

  const pickMember = (id: string) => {
    const m = members.find((x) => x.id === id);
    setForm((f) =>
      f && {
        ...f,
        teamMemberId: id,
        currency: m ? m.currency : f.currency,
        amount: m && m.fixedSalary && f.kind === "salary" ? String(m.fixedSalary) : f.amount,
      },
    );
  };

  const submitForm = async () => {
    if (!form) return;
    setBusy(true);
    setNotice(null);
    try {
      const common = {
        paidAt: form.paidAt,
        amount: Number(form.amount),
        currency: form.currency.toUpperCase(),
        method: form.method,
        reference: form.reference || null,
        notes: form.notes || null,
        // #1141: el costo del periodo en pesos usa este tipo de cambio si se captura.
        exchangeRate: form.currency.toUpperCase() !== "MXN" && Number(form.exchangeRate) > 0 ? Number(form.exchangeRate) : null,
      };
      if (form.id) await updatePayment(form.id, common);
      else
        await createPayment({
          ...common,
          teamMemberId: form.teamMemberId,
          kind: form.kind,
          period: `${form.month}-${form.half}`,
        });
      setForm(null);
      await load();
    } catch (err) {
      fail(err, "payroll.payments.saveError");
    } finally {
      setBusy(false);
    }
  };

  const submitRun = async () => {
    if (!run) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await runPayments({
        period: `${run.month}-${run.half}`,
        paidAt: run.paidAt,
        method: run.method,
        reference: run.reference || undefined,
      });
      setRun(null);
      setNotice(t("payroll.payments.runResult", { created: res.created.length, skipped: res.skipped.length }));
      await load();
    } catch (err) {
      fail(err, "payroll.payments.saveError");
    } finally {
      setBusy(false);
    }
  };

  const input = `w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.bg} ${tc.text}`;
  const label = `mb-1 block text-xs font-medium ${tc.textSecondary}`;
  const btn = "inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors";
  const ghost = `${btn} border ${tc.border} ${tc.text} ${tc.buttonHover}`;

  const periodInputs = (month: string, half: Half, set: (month: string, half: Half) => void) => (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={label}>{t("payroll.payments.period")}</label>
        <input type="month" className={input} value={month} onChange={(e) => set(e.target.value, half)} />
      </div>
      <div>
        <label className={label}>&nbsp;</label>
        <select className={input} value={half} onChange={(e) => set(month, e.target.value as Half)}>
          <option value="Q1">{t("payroll.payments.half1")}</option>
          <option value="Q2">{t("payroll.payments.half2")}</option>
          <option value="M">{t("payroll.payments.month")}</option>
        </select>
      </div>
    </div>
  );

  const methodSelect = (value: PaymentMethod, set: (m: PaymentMethod) => void) => (
    <select className={input} value={value} onChange={(e) => set(e.target.value as PaymentMethod)}>
      {METHODS.map((m) => (
        <option key={m} value={m}>
          {t(`payroll.payments.methods.${m}`)}
        </option>
      ))}
    </select>
  );

  return (
    <div className="w-full overflow-y-auto p-4 md:p-6" data-testid="payroll-payments">
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="payment-proof-input"
      />

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className={`text-xl font-semibold ${tc.text}`}>
            <i className="ri-wallet-3-line mr-2" />
            {t("payroll.payments.title")}
          </h2>
          <p className={`text-sm ${tc.textSecondary}`}>
            {t(isAdmin ? "payroll.payments.subtitleAdmin" : "payroll.payments.subtitleOwn")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className={label}>{t("payroll.payments.year")}</label>
            <select className={input} value={year} onChange={(e) => setYear(e.target.value)} data-testid="payment-year">
              <option value="">{t("payroll.payments.all")}</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          {isAdmin && (
            <>
              <div>
                <label className={label}>{t("payroll.payments.person")}</label>
                <select className={input} value={person} onChange={(e) => setPerson(e.target.value)}>
                  <option value="">{t("payroll.payments.all")}</option>
                  {people.map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className={`${btn} border ${tc.border} ${tc.text} ${tc.buttonHover} py-2`}
                onClick={() => setRun({ month: thisMonth(), half: currentHalf(), paidAt: today(), method: "nvio", reference: "" })}
              >
                <i className="ri-team-line" />
                {t("payroll.payments.run")}
              </button>
              <button
                type="button"
                className={`${btn} bg-primary-500 py-2 text-white hover:bg-primary-400`}
                onClick={() => setForm(emptyForm())}
              >
                <i className="ri-add-line" />
                {t("payroll.payments.new")}
              </button>
            </>
          )}
        </div>
      </div>

      {notice && (
        <div className="mb-4 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-2 text-sm text-sky-500">
          <i className="ri-information-line mr-1" />
          {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {totals.map(([currency, total]) => (
            <div key={currency} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`text-xs ${tc.textSecondary}`}>
                {year ? t("payroll.payments.totalYear", { year }) : t("payroll.payments.amount")}
              </p>
              <p className="text-lg font-semibold text-emerald-500">{formatAmount(total, currency)}</p>
              <p className={`text-xs ${tc.textSecondary}`}>
                {t("payroll.payments.count", { count: visible.filter((r) => r.currency === currency).length })}
              </p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
          <i className="ri-loader-4-line mr-1 animate-spin" />
          {t("payroll.loading")}
        </div>
      ) : visible.length === 0 ? (
        <div className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
          <p>{t("payroll.payments.empty")}</p>
          {!isAdmin && <p className="mt-2 text-xs">{t("payroll.payments.noMember")}</p>}
        </div>
      ) : (
        <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="w-full overflow-x-auto">
            <table className="w-full text-xs" data-testid="payments-table">
              <thead className="bg-primary-500 text-white">
                <tr>
                  <th className="p-3 text-left font-bold">{t("payroll.payments.period")}</th>
                  {isAdmin && <th className="p-3 text-left font-bold">{t("payroll.payments.person")}</th>}
                  <th className="p-3 text-left font-bold">{t("payroll.payments.paidAt")}</th>
                  <th className="p-3 text-left font-bold">{t("payroll.payments.kind")}</th>
                  <th className="p-3 text-left font-bold">{t("payroll.payments.method")}</th>
                  <th className="p-3 text-right font-bold">{t("payroll.payments.amount")}</th>
                  <th className="p-3 text-center font-bold">{t("payroll.payments.proof")}</th>
                  <th className="p-3 text-center font-bold">{t("payroll.payments.receipt")}</th>
                  {isAdmin && <th className="p-3 text-center font-bold">{t("payroll.actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((p, i) => (
                  <tr
                    key={p.id}
                    className={
                      isDarkMode
                        ? i % 2 === 0
                          ? "bg-[#232323] text-white"
                          : "bg-[#1C1C1C] text-white"
                        : i % 2 === 0
                          ? "bg-gray-50 text-gray-900"
                          : "bg-white text-gray-900"
                    }
                  >
                    <td className="p-2 whitespace-nowrap">{periodLabel(p.period)}</td>
                    {isAdmin && <td className="p-2">{p.teamMemberName || p.teamMemberId}</td>}
                    <td className="p-2 whitespace-nowrap">{p.paidAt}</td>
                    <td className="p-2">{t(`payroll.payments.kinds.${p.kind}`)}</td>
                    <td className="p-2">
                      {t(`payroll.payments.methods.${p.method}`)}
                      {p.reference && <span className={`block text-[10px] ${tc.textSecondary}`}>{p.reference}</span>}
                    </td>
                    <td className="p-2 text-right font-semibold whitespace-nowrap">{formatAmount(p.amount, p.currency)}</td>
                    <td className="p-2 text-center">
                      {p.proof ? (
                        <button type="button" className={ghost} onClick={() => downloadProof(p)} title={p.proof.name}>
                          <i className="ri-file-download-line" />
                          {t("payroll.payments.download")}
                        </button>
                      ) : (
                        <span className={`text-[11px] ${tc.textSecondary}`}>{t("payroll.payments.noProof")}</span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <button type="button" className={ghost} onClick={() => downloadReceipt(p)}>
                        <i className="ri-file-pdf-2-line" />
                        PDF
                      </button>
                    </td>
                    {isAdmin && (
                      <td className="p-2">
                        <div className="flex justify-center gap-1">
                          <button
                            type="button"
                            className={ghost}
                            disabled={busy}
                            onClick={() => pickProof(p.id)}
                            title={t(p.proof ? "payroll.payments.replaceProof" : "payroll.payments.uploadProof")}
                          >
                            <i className="ri-upload-2-line" />
                          </button>
                          <button type="button" className={ghost} onClick={() => openEdit(p)} title={t("payroll.payments.edit")}>
                            <i className="ri-pencil-line" />
                          </button>
                          <button type="button" className={`${ghost} text-red-500`} onClick={() => remove(p)} title={t("payroll.payments.delete")}>
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {form && (
        <Modal
          title={form.id ? t("payroll.payments.edit") : t("payroll.payments.new")}
          onClose={() => setForm(null)}
          themeClasses={tc}
          content={
            <div className="space-y-3 py-2">
              <div>
                <label className={label}>{t("payroll.payments.person")}</label>
                <select className={input} value={form.teamMemberId} disabled={Boolean(form.id)} onChange={(e) => pickMember(e.target.value)}>
                  <option value="">—</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>{t("payroll.payments.kind")}</label>
                <select
                  className={input}
                  value={form.kind}
                  disabled={Boolean(form.id)}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as PaymentKind })}
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`payroll.payments.kinds.${k}`)}
                    </option>
                  ))}
                </select>
              </div>
              {!form.id && periodInputs(form.month, form.half, (month, half) => setForm({ ...form, month, half }))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>{t("payroll.payments.paidAt")}</label>
                  <input type="date" className={input} value={form.paidAt} onChange={(e) => setForm({ ...form, paidAt: e.target.value })} />
                </div>
                <div>
                  <label className={label}>{t("payroll.payments.method")}</label>
                  {methodSelect(form.method, (method) => setForm({ ...form, method }))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>{t("payroll.payments.amount")}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={input}
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
                <div>
                  <label className={label}>{t("payroll.payments.currency")}</label>
                  <input
                    className={input}
                    maxLength={3}
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>
              {form.currency.toUpperCase() !== "MXN" && (
                <div>
                  <label className={label}>{t("payroll.payments.exchangeRate")}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    className={input}
                    placeholder={t("payroll.payments.exchangeRateHint")}
                    value={form.exchangeRate}
                    onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })}
                  />
                </div>
              )}
              <div>
                <label className={label}>{t("payroll.payments.reference")}</label>
                <input className={input} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </div>
              <div>
                <label className={label}>{t("payroll.payments.notes")}</label>
                <textarea className={input} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className={ghost} onClick={() => setForm(null)}>
                  {t("payroll.payments.cancel")}
                </button>
                <button
                  type="button"
                  className={`${btn} text-white`}
                  style={{ background: brand.primary }}
                  disabled={busy || !form.teamMemberId || !(Number(form.amount) > 0)}
                  onClick={submitForm}
                >
                  {t("payroll.payments.save")}
                </button>
              </div>
            </div>
          }
        />
      )}

      {run && (
        <Modal
          title={t("payroll.payments.run")}
          onClose={() => setRun(null)}
          themeClasses={tc}
          content={
            <div className="space-y-3 py-2">
              <p className={`text-xs ${tc.textSecondary}`}>{t("payroll.payments.runHint")}</p>
              {periodInputs(run.month, run.half, (month, half) => setRun({ ...run, month, half }))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>{t("payroll.payments.paidAt")}</label>
                  <input type="date" className={input} value={run.paidAt} onChange={(e) => setRun({ ...run, paidAt: e.target.value })} />
                </div>
                <div>
                  <label className={label}>{t("payroll.payments.method")}</label>
                  {methodSelect(run.method, (method) => setRun({ ...run, method }))}
                </div>
              </div>
              <div>
                <label className={label}>{t("payroll.payments.reference")}</label>
                <input className={input} value={run.reference} onChange={(e) => setRun({ ...run, reference: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className={ghost} onClick={() => setRun(null)}>
                  {t("payroll.payments.cancel")}
                </button>
                <button type="button" className={`${btn} text-white`} style={{ background: brand.primary }} disabled={busy} onClick={submitRun}>
                  {t("payroll.payments.run")}
                </button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
